<script setup lang="ts">
import { useAsyncData } from "#app";
import { setMeta } from "~/utils/meta";
import { useBlogPostStore } from "~/stores/blog/blogPost";
import { useRoute } from "vue-router";
import ContentTextEditor from "~/components/content/ContentTextEditor.vue";
import { ContentTypeEnum } from "~/types/enum/ContentTypeEnum";
import ContentTitleMediumTextAndRightImage from "~/components/content/ContentTitleMediumTextAndRightImage.vue";

const blogPostStore = useBlogPostStore();
const slug = String(useRoute().params?.slug);

const {data} = await useAsyncData('blogPostStore', async () => await blogPostStore.fetchList(slug));

if (typeof blogPostStore.error === "object") {
    throw blogPostStore.error;
}

if (blogPostStore.seo !== undefined) {
    setMeta(blogPostStore.seo);
}
</script>

<template>
    <div class="container space-y-16 text-black dark:text-white blog-post">
        <div class="flex flex-col text-gray-700 dark:text-white">
            <div class="text-base leading-7 lg:w-7/12 my-4 mx-auto">
                <p v-if="blogPostStore.tags" class="font-semibold leading-7 text-indigo-600">{{ blogPostStore.tags }}</p>
                <h1 class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{{ blogPostStore.title }}</h1>
                <p class="code-block mt-4">
                    fetch("/<nuxt-link to="/blog" class="button button-code">posts</nuxt-link>/{{ blogPostStore.slug }}")
                </p>
                <div v-if="blogPostStore.previewText" class="mt-6 text-xl leading-8">
                    <p>{{ blogPostStore.previewText }}</p>
                </div>
            </div>

            <template v-if="blogPostStore.content !== undefined && blogPostStore.content.length"
                      v-for="content in blogPostStore.content"
            >
                <content-text-editor v-if="content.type === ContentTypeEnum.TextEditor" :content="content"></content-text-editor>
                <content-title-medium-text-and-right-image v-if="content.type === ContentTypeEnum.TitleMediumTextAndRightImage"
                                                           :content="content"></content-title-medium-text-and-right-image>
            </template>
        </div>
    </div>
</template>

<style lang="scss">
.blog-post {
    p, pre {
        margin-bottom: 1rem;
    }

    .text-base {
        h2 {
            font-weight: bold;
            font-size: x-large;

            &:not(.tracking-tight) {
                margin-top: 3rem;
                margin-bottom: 1.5rem;
            }
        }
    }

    img {
        object-fit: contain;
        max-width: 100%;
        max-height: 100%;
    }

    code.hljs {
        max-width: 100%;
        overflow-x: auto;
    }

    ul, ol {
        list-style: revert;
        margin: revert;
        padding: revert;

        li {
            padding: 0.5rem 0;
        }
    }
}
</style>